/**
 * 차시 장면 해냄(오늘의 미션 scene:<차시> · PICNIC-MB-FIELD-001 §6.13) — 놀이터가 어떤 차시 장면을 해냈다고 기록할지 한 곳에서 정한다.
 *
 * 장면마다 해냄을 아는 곳이 다르다:
 *  - 첫 묶음 넷(영화 · 박동 · 표정 코스 · 트랙) — 화면의 장면 상태(local)
 *  - 센서 월드(lib/sensorWorld) — 장면별 센서 상태
 *  - 나머지 차시 장면(lib/lessons — 기본 · 리모컨 · 무대) — lessonDone
 *  - AI 차시 놀이(lib/aiQuest) — 놀이 상태
 *  - 라디오 광장 차시(RADIO_LESSONS) — 그 차시로 연 광장에서 주고받기 3번
 *
 * **지금 연 장면만** 해냄으로 친다(2026-10-06): lib/lessons 의 상태는 열린 장면과 상관없이 매 틱 함께 나아가서, 다른 장면에서 놀다가
 * 그 장면의 조건이 저절로 채워질 수 있다(그림 하나면 픽셀 갤러리). 미션은 '놀이터에서 ○○ 해내기'다 — 그 장면을 열고 해낸 것만 센다.
 */
import type { SceneId } from "./scenes";
import { AI_LESSONS, RADIO_LESSONS, SCENES } from "./scenes";
import { sceneOfLesson } from "./sensorWorld";
import { lessonDone } from "./lessons";
import type { Lessons } from "./lessons";

/** 첫 묶음 넷 — 화면의 장면 상태로 안다 */
export const LOCAL_DONE = ["movie", "heartbeat", "faceCourse", "track"] as const;
export type LocalDone = Readonly<Record<(typeof LOCAL_DONE)[number], boolean>>;

/** 장면이 해냄을 어디서 아는가(없으면 null — 그 차시 미션은 끝낼 수 없다) */
export function doneSource(id: SceneId, lessons: Lessons): "local" | "sensor" | "lesson" | null {
  if ((LOCAL_DONE as readonly string[]).includes(id)) return "local";
  if (sceneOfLesson(id)) return "sensor";
  return lessonDone(id, lessons) === null ? null : "lesson";
}

/** 놀이터 장면이 있는 차시 코드 전부(장면 · 라디오 광장 · AI) — 서버 SCENE_LESSONS · Campus 장면 목록과 같은 94개 */
export const SCENE_LESSON_CODES: readonly string[] = [
  ...SCENES.flatMap((s) => (s.lesson ? [s.lesson] : [])),
  ...RADIO_LESSONS,
  ...AI_LESSONS,
];

export interface DoneInput {
  /** 지금 연 장면 */
  readonly scene: SceneId;
  readonly local: LocalDone;
  readonly lessons: Lessons;
  readonly sensorDone: (id: SceneId) => boolean;
  /** 지금 연 AI 차시 놀이(없으면 null)와 해냈는가 */
  readonly quest: { readonly id: string; readonly done: boolean } | null;
  /** 라디오 광장을 연 차시(L27 …)와 주고받기 3번을 채웠는가 */
  readonly radio: { readonly lesson: string | null; readonly exchanged: boolean };
}

/** 지금 해낸 차시 장면(지금 연 장면 하나 — 없으면 빈 목록) */
export function doneLessonsOf(a: DoneInput): string[] {
  if (a.scene === "aiCards") return a.quest?.done ? [a.quest.id] : [];
  if (a.scene === "plaza") return a.radio.lesson && a.radio.exchanged ? [a.radio.lesson] : [];
  const sc = SCENES.find((x) => x.id === a.scene);
  if (!sc?.lesson) return [];
  const src = doneSource(sc.id, a.lessons);
  const done = src === "local" ? a.local[sc.id as keyof LocalDone] : src === "sensor" ? a.sensorDone(sc.id) : src === "lesson" ? lessonDone(sc.id, a.lessons) === true : false;
  return done ? [sc.lesson] : [];
}

/**
 * 오늘의 미션이 바라는 차시 장면(scene:LE04 → "LE04") — 아직 못 해낸 것이 먼저, 다 해냈으면 첫 것. 놀이터 목표가 아니거나 모르는 차시면 null.
 * 놀이터가 그 장면으로 열린다(MicrobitScreen — 주소 ?play= 가 없을 때). 아이 작품의 차시와 회차가 다를 때(1회차 작품으로 4회차 미션) 미션이 이긴다.
 */
export function missionSceneLesson(m: { readonly goals: readonly { readonly modeId: string; readonly presetKey: string; readonly state: string }[] } | null | undefined): string | null {
  const codes = (m?.goals ?? [])
    .filter((g) => g.modeId === "microbit-playground" && g.presetKey.startsWith("scene:"))
    .map((g) => ({ code: g.presetKey.slice(6), open: g.state !== "verified" }))
    .filter((g) => SCENE_LESSON_CODES.includes(g.code));
  return (codes.find((g) => g.open) ?? codes[0])?.code ?? null;
}
